import { logger } from '../logger';
import type { SupportedLanguages } from '../../i18n';

const STORAGE_KEY = 'app_stay_dates';
const CHECKOUT_ALERT_SHOWN_KEY = 'checkout_alert_shown_date';

export interface StayDates {
  startDate: string; // ISO string
  endDate: string; // ISO string
  lastUpdated: string; // ISO string
}

class StayService {
  private static instance: StayService;
  private stayDates: StayDates | null = null;

  private constructor() {
    this.loadStayDates();
  }

  public static getInstance(): StayService {
    if (!StayService.instance) {
      StayService.instance = new StayService();
    }
    return StayService.instance;
  }

  private loadStayDates(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.stayDates = JSON.parse(stored);
      }
    } catch (error) {
      logger.error('[STAY_DATES_ERROR] Error loading stay dates', {
        category: 'Stay',
        data: { error, storageKey: STORAGE_KEY }
      });
      this.stayDates = null;
    }
  }

  private saveStayDates(): void {
    try {
      if (this.stayDates) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.stayDates));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      logger.error('[STAY_DATES_SAVE_ERROR] Error saving stay dates', {
        category: 'Stay',
        data: { error, storageKey: STORAGE_KEY }
      });
    }
  }

  public getStayDates(): StayDates | null {
    return this.stayDates;
  }

  public setStayDates(startDate: Date, endDate: Date): void {
    this.stayDates = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      lastUpdated: new Date().toISOString()
    };
    this.saveStayDates();
    
    // Clear the checkout alert shown flag when setting new dates
    localStorage.removeItem(CHECKOUT_ALERT_SHOWN_KEY);
  }

  public hasStayDates(): boolean {
    return this.stayDates !== null;
  }

  public clearStayDates(): void {
    this.stayDates = null;
    this.saveStayDates();
    
    // Clear the checkout alert shown flag when clearing dates
    localStorage.removeItem(CHECKOUT_ALERT_SHOWN_KEY);
  }

  public getDefaultDates(): { startDate: Date; endDate: Date } {
    const now = new Date();
    
    // Start date is today at current time
    const startDate = new Date(now);
    
    // End date is tomorrow at midnight
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(23, 59, 59, 999);
    
    return { startDate, endDate };
  }

  public formatStayDates(language: SupportedLanguages): { formattedStart: string; formattedEnd: string } | null {
    if (!this.stayDates) return null;
    
    const startDate = new Date(this.stayDates.startDate);
    const endDate = new Date(this.stayDates.endDate);
    
    const formattedStart = new Intl.DateTimeFormat(language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(startDate);
    
    const formattedEnd = new Intl.DateTimeFormat(language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(endDate);
    
    return { formattedStart, formattedEnd };
  }

  public areDatesExpired(): boolean {
    if (!this.stayDates) return false;
    
    const now = new Date();
    const endDate = new Date(this.stayDates.endDate);
    
    return endDate < now;
  }

  /**
   * Checks if today is the checkout day (last day of stay)
   * @returns boolean indicating if today is the checkout day
   */
  public isCheckoutDay(): boolean {
    if (!this.stayDates) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const endDate = new Date(this.stayDates.endDate);
    endDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Check if today is the checkout day
    return today.getTime() === endDate.getTime();
  }

  /**
   * Checks if checkout alert has been shown today
   * @returns boolean indicating if checkout alert has been shown today
   */
  public hasCheckoutAlertBeenShownToday(): boolean {
    const lastShown = localStorage.getItem(CHECKOUT_ALERT_SHOWN_KEY);
    if (!lastShown) return false;
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return lastShown === today;
  }

  /**
   * Marks checkout alert as shown for today
   */
  public markCheckoutAlertAsShown(): void {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    localStorage.setItem(CHECKOUT_ALERT_SHOWN_KEY, today);
  }
}

export const stayService = StayService.getInstance();